/**
 * Утилиты для работы с IPFS URL
 * Поддержка множественных IPFS gateways с автоматическим fallback
 */

// Список IPFS gateways для fallback (в порядке приоритета)
// dweb.link обычно быстрее и надежнее, чем ipfs.io
// cloudflare-ipfs.com удален, так как часто возвращает ERR_NAME_NOT_RESOLVED
export const IPFS_GATEWAYS = [
  'https://dweb.link/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://nftstorage.link/ipfs/',
  'https://ipfs.filebase.io/ipfs/',
  // Резервный вариант (может быть недоступен в некоторых регионах)
  'https://cloudflare-ipfs.com/ipfs/',
] as const;

/**
 * Извлекает IPFS hash из различных форматов URL
 */
export function extractIpfsHash(url: string): string | null {
  if (!url) return null;

  // ipfs:// протокол
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', '');
  }

  // gateway.pinata.cloud/ipfs/HASH или /ipfs/HASH/path
  const pinataMatch = url.match(/gateway\.pinata\.cloud\/ipfs\/([a-zA-Z0-9]+(?:\/[^?#]*)?)/);
  if (pinataMatch) {
    return pinataMatch[1];
  }

  // Формат: hash.ipfs.domain.com/path
  const subdomain = url.match(/([a-zA-Z0-9]+)\.ipfs\.[^/]+(?:\/(.+))?/);
  if (subdomain) {
    const [, hash, path] = subdomain;
    return path ? `${hash}/${path}` : hash;
  }

  // Формат: domain.com/ipfs/hash/path
  const pathMatch = url.match(/\/ipfs\/([a-zA-Z0-9]+(?:\/[^?#]*)?)/);
  if (pathMatch) {
    return pathMatch[1];
  }

  // Просто IPFS hash (Qm... или baf...)
  if (/^(?:Qm[a-zA-Z0-9]{44}|ba[a-zA-Z0-9]{57,})(?:\/.*)?$/.test(url)) {
    return url;
  }

  // Если уже HTTP(S) URL и не похож на IPFS, возвращаем как есть
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return null; // Не IPFS URL
  }

  return null;
}

/**
 * Конвертирует любой IPFS URL в стандартный gateway URL
 */
export function normalizeIpfsUrl(url: string, gatewayIndex: number = 0): string {
  if (!url) return url;

  // Если это обычный HTTP(S) URL (не IPFS)
  if ((url.startsWith('http://') || url.startsWith('https://')) && !url.includes('ipfs')) {
    return url;
  }

  const hash = extractIpfsHash(url);
  
  if (!hash) {
    // Если не смогли извлечь hash, но это HTTP URL, возвращаем как есть
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return url;
  }

  // Используем указанный gateway
  const gateway = IPFS_GATEWAYS[gatewayIndex] || IPFS_GATEWAYS[0];
  return `${gateway}${hash}`;
}

/**
 * Получает URL для изображения с поддержкой fallback
 * @param url - исходный URL
 * @param attemptIndex - номер попытки (для переключения между gateways)
 */
export function getIpfsImageUrl(url: string | null | undefined, attemptIndex: number = 0): string | null {
  if (!url) return null;

  // Если это не IPFS URL, возвращаем как есть
  if ((url.startsWith('http://') || url.startsWith('https://')) && !url.includes('ipfs')) {
    return url;
  }

  return normalizeIpfsUrl(url, attemptIndex % IPFS_GATEWAYS.length);
}

/**
 * Проверяет, является ли URL ссылкой на IPFS
 */
export function isIpfsUrl(url: string): boolean {
  if (!url) return false;
  
  return (
    url.startsWith('ipfs://') ||
    url.includes('.ipfs.') ||
    url.includes('/ipfs/') ||
    /^(?:Qm[a-zA-Z0-9]{44}|ba[a-zA-Z0-9]{57,})/.test(url)
  );
}

/**
 * Получает все возможные варианты URL для IPFS hash
 */
export function getAllIpfsUrls(url: string): string[] {
  const hash = extractIpfsHash(url);
  if (!hash) return [url];

  return IPFS_GATEWAYS.map(gateway => `${gateway}${hash}`);
}




